import {
  forwardRef,
  useId,
  type ChangeEvent,
  type FocusEvent,
  type HTMLInputTypeAttribute,
  type InputHTMLAttributes,
  type ReactNode
} from 'react';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import InputBase from '@mui/material/InputBase';
import InputLabel from '@mui/material/InputLabel';
import type { FieldAppearance } from '../fieldAppearance';

type InputElement = HTMLInputElement | HTMLTextAreaElement;

function clampNumberInputToMax(
  event: ChangeEvent<InputElement>,
  type: HTMLInputTypeAttribute | undefined,
  inputProps?: InputHTMLAttributes<InputElement>
): void {
  if (type !== 'number' || inputProps?.max == null) return;
  const raw = event.target.value;
  if (raw === '') return;
  const n = Number(raw);
  const maxN = Number(inputProps.max);
  if (!Number.isFinite(n) || !Number.isFinite(maxN) || n <= maxN) return;
  event.target.value = String(maxN);
}

function resolveInputEndAdornment(endIcon: ReactNode | undefined, inputEndAdornment: ReactNode | undefined) {
  if (!endIcon && !inputEndAdornment) {
    return undefined;
  }

  if (!inputEndAdornment) {
    return (
      <InputAdornment position="end" className="SmInputAdornment-root">
        {endIcon}
      </InputAdornment>
    );
  }

  if (!endIcon) {
    return (
      <InputAdornment position="end" className="SmInputAdornment-root">
        {inputEndAdornment}
      </InputAdornment>
    );
  }

  return (
    <InputAdornment position="end" className="SmInputAdornment-root SmInputAdornment-combinedEnd">
      <span className="SmInputAdornment-customEndIcon">{endIcon}</span>
      {inputEndAdornment}
    </InputAdornment>
  );
}

/**
 * Design-system Input (styled InputBase).
 * Renders `FormControl variant="standard"` + `InputLabel shrink` + styled InputBase.
 * Set `multiline` for textarea behavior (same styling, `rows` / `minRows` / `maxRows`).
 */
export type InputProps = {
  label?: string;
  /**
   * `default` — grey field + label above.
   * `plain` — white field + label above (same layout as default).
   * `dashed` — no background/border, dashed bottom border only (e.g. an inline "add X" trigger).
   * `borderless` — no background, no border at all (e.g. an inline-editable title/heading).
   * @default 'default'
   */
  appearance?: FieldAppearance;
  /** Controls the component width via MUI FormControl. @default true */
  fullWidth?: boolean;
  placeholder?: string;
  defaultValue?: unknown;
  value?: unknown;
  onChange?: (event: ChangeEvent<InputElement> | ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (event: FocusEvent<InputElement> | FocusEvent<HTMLTextAreaElement>) => void;
  onFocus?: (event: FocusEvent<InputElement> | FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string | ReactNode;
  /** Icon node only (e.g. `<SearchIcon />`); layout and sizing are applied inside `Input`. Ignored when `multiline`. */
  startIcon?: ReactNode;
  /** Icon node only; layout and sizing are applied inside `Input`. Ignored when `multiline`. */
  endIcon?: ReactNode;
  /** Pre-built start adornment (e.g. MUI Autocomplete tags). Takes precedence over `startIcon`. Ignored when `multiline`. */
  inputStartAdornment?: ReactNode;
  /** Pre-built end adornment (e.g. MUI Autocomplete indicators). Combined with `endIcon` when both are set. */
  inputEndAdornment?: ReactNode;
  name?: string;
  autoComplete?: string;
  type?: React.HTMLInputTypeAttribute;
  readOnly?: boolean;
  maxLength?: number;
  minLength?: number;
  required?: boolean;
  autoFocus?: boolean;
  /** Renders a multiline textarea instead of a single-line input. */
  multiline?: boolean;
  rows?: number;
  minRows?: number;
  maxRows?: number;
  /** Merged into the native `<input>` / `<textarea>` (e.g. `id`, `aria-*` from Autocomplete). */
  inputProps?: InputHTMLAttributes<InputElement>;
  inputRef?: React.Ref<InputElement>;
};

export const Input = forwardRef<HTMLDivElement, InputProps>(function Input(
  {
    label,
    appearance = 'default',
    fullWidth = true,
    placeholder,
    defaultValue,
    value,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    required = false,
    disabled = false,
    error = false,
    helperText,
    startIcon,
    endIcon,
    inputStartAdornment,
    inputEndAdornment,
    name,
    autoComplete,
    type = 'text',
    readOnly,
    maxLength,
    minLength,
    autoFocus = false,
    multiline = false,
    rows,
    minRows,
    maxRows,
    inputProps: extraInputProps,
    inputRef
  },
  ref
) {
  const fieldId = useId();
  const isPlain = appearance === 'plain';
  const isDashed = appearance === 'dashed';
  const isBorderless = appearance === 'borderless';
  const hasLabel = Boolean(label?.trim());
  // No label to carry the asterisk (e.g. borderless inline inputs) — mark the placeholder instead.
  const resolvedPlaceholder = required && !hasLabel && placeholder ? `${placeholder} *` : placeholder;
  const inputBaseClassName = [
    'SmInputBase-root',
    isPlain ? 'SmInputBase-appearance-plain' : null,
    isDashed ? 'SmInputBase-appearance-dashed' : null,
    isBorderless ? 'SmInputBase-appearance-borderless' : null,
    multiline ? 'SmTextAreaBase-root' : null
  ]
    .filter(Boolean)
    .join(' ');
  const startAdornment = !multiline
    ? (inputStartAdornment ??
        (startIcon ? (
          <InputAdornment position="start" className="SmInputAdornment-root">
            {startIcon}
          </InputAdornment>
        ) : undefined))
    : undefined;
  const endAdornment = !multiline ? resolveInputEndAdornment(endIcon, inputEndAdornment) : undefined;

  const resolvedDisabled = readOnly || disabled;

  return (
    <FormControl variant="standard" fullWidth={fullWidth} disabled={resolvedDisabled} error={error} required={required}>
      {hasLabel ? (
        <InputLabel className="SmInputLabel-root" shrink htmlFor={fieldId}>
          {label}
        </InputLabel>
      ) : null}
      <InputBase
        className={inputBaseClassName}
        id={fieldId}
        ref={ref}
        fullWidth={fullWidth}
        disabled={resolvedDisabled}
        required={required}
        autoFocus={autoFocus}
        placeholder={resolvedPlaceholder}
        defaultValue={defaultValue}
        value={value}
        onChange={event => {
          clampNumberInputToMax(event, type, extraInputProps);
          onChange?.(event);
        }}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        startAdornment={startAdornment}
        endAdornment={endAdornment}
        name={name}
        autoComplete={autoComplete}
        type={multiline ? undefined : type}
        multiline={multiline}
        rows={rows}
        minRows={minRows}
        maxRows={maxRows}
        inputRef={inputRef}
        inputProps={{
          readOnly,
          maxLength,
          minLength,
          ...extraInputProps,
          ...((isPlain || isDashed || isBorderless) && !extraInputProps?.['aria-label']
            ? { 'aria-label': hasLabel ? label : (resolvedPlaceholder ?? '') }
            : {})
        }}
      />
      {helperText ? <FormHelperText className="SmInputHelperText-root">{helperText}</FormHelperText> : null}
    </FormControl>
  );
});
